import React, { useCallback, useEffect } from "react";

import { Drawer } from "@common/components/Drawer/Drawer";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { BaseTable } from "@common/components/ShakeTable/BaseTable";
import { Spinner } from "@common/components/Spinner/Spinner";
import { PlatformName } from "@common/models";
import { useActivityHistoryFilter } from "@main/pages/shared/consumers/useActivityHistoryFilter";
import { useAppSelectionContext } from "@public/context/App/AppSelectionContext";
import { ActivityHistoryFilterView } from "public/pages/shared/components/ActivityHistoryTable/SearchAndFilter/ActivityHistoryFilterView";

import { useActivityHistoryApiConsumer } from "../../consumers/useActivityHistoryApiConsumer";
import { resolveEmptyStateView } from "../../helpers/resolveEmptyStateView";
import { EmptyStateViewConf, EmptyTableView } from "../EmptyTableView/EmptyTableView";
import { LogDetails } from "./LogDetails/LogDetails";

interface Props {
  ticketId: string;
  type: "user_feedback";
  hasLogs?: boolean;
}

export const ActivityHistoryTable = ({ ticketId, type, hasLogs }: Props) => {
  const { selectedApp } = useAppSelectionContext();

  let isWebApp: boolean | undefined = undefined;
  if (selectedApp) isWebApp = selectedApp.platform.name === PlatformName.WEB;

  const { filter, updateFilter } = useActivityHistoryFilter(isWebApp ?? false);

  const { getNextLog, getPreviousLog, items, loading, activityTableProps, detailLog, setDetailLog, error } =
    useActivityHistoryApiConsumer({
      type: type,
      ticketId: ticketId,
      hasLogs,
      filter,
      isWebApp,
    });

  const handleNavigateToNext = useCallback(() => {
    if (!detailLog) {
      return;
    }
    const nextDetailLog = getNextLog(detailLog.id);
    nextDetailLog && setDetailLog(nextDetailLog);
  }, [getNextLog, detailLog]);

  const handleNavigateToPrevious = useCallback(() => {
    if (!detailLog) {
      return;
    }
    const previousDetailLog = getPreviousLog(detailLog.id);
    previousDetailLog && setDetailLog(previousDetailLog);
  }, [getPreviousLog, setDetailLog, detailLog]);

  const handleLogClose = useCallback(() => {
    setDetailLog(undefined);
    activityTableProps.table.resetRowSelection();
  }, [setDetailLog]);

  useEffect(() => {
    if (detailLog) {
      const selectedRow = activityTableProps.table.getSelectedRowModel().rows?.[0];
      if (selectedRow?.original?.id === detailLog?.id) return;

      activityTableProps.table.resetRowSelection();
      const row = activityTableProps.table.getRowModel().rows.find((a) => a.original.id === detailLog?.id);
      if (row) {
        row?.toggleSelected(true);
      } else {
        setDetailLog(undefined);
      }
    }
    // eslint-disable-next-line
  }, [detailLog, filter]);

  useEffect(() => {
    handleLogClose();
  }, [handleLogClose]);

  const hasItems = Boolean(items && items.length > 0);
  const emptyViewConfig = resolveEmptyStateView(loading, "logs", hasItems, true, error) as
    | EmptyStateViewConf
    | undefined;

  const isInitialLoading = typeof items == "undefined" && loading;

  return (
    <>
      <PageWrap style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <ActivityHistoryFilterView
          filter={filter}
          setFilter={updateFilter}
        />
        {emptyViewConfig && (
          <EmptyTableView
            withFilters
            {...emptyViewConfig}
          />
        )}
        {isInitialLoading && <Spinner />}
        {!emptyViewConfig && !isInitialLoading && <BaseTable {...activityTableProps} />}
      </PageWrap>
      <Drawer open={!!detailLog}>
        {detailLog && (
          <LogDetails
            log={detailLog}
            navigateToNext={handleNavigateToNext}
            navigateToPrevious={handleNavigateToPrevious}
            closeDrawer={handleLogClose}
          />
        )}
      </Drawer>
    </>
  );
};
